<template>
  <div
    class="modal"
    :class="{ modal_mini: mini, 'modal--popup': popup }"
    ref="modal"
  >
    <transition :name="popup ? 'popup' : 'translate'">
      <div class="modal__show-content" v-if="showContent">
        <div class="modal__backdrop" @click="handleClose"></div>

        <button class="modal__close" @click="handleClose">
          <icon name="modal-close" v-if="!back" />
          <icon name="back-window" v-else />
        </button>

        <div class="modal__content" ref="modalContent">
          <button class="modal__close modal__close_mobile" @click="handleClose">
            <icon name="modal-close" v-if="!back" />
            <icon name="back-window" v-else />
          </button>
          <slot />
        </div>
      </div>
    </transition>

    <Popup
      v-if="showCloseConfirmation"
      agree
      withInModal
      @confirm="confirmClosePopup"
      @close="cancelClose"
    >
      {{ confirmCloseText }}
    </Popup>
  </div>
</template>

<script>
import modalHolder from "@/helpers/modalHolder";
import detectIE from "@/helpers/detectIE";
import Popup from "@v2/shared/ui/components/popup/Popup.vue";

export default {
  name: "Modal",

  components: {
    Popup
  },

  props: {
    test: {
      type: Boolean,
      default: false
    },

    mini: {
      type: Boolean,
      default: false
    },

    popup: {
      type: Boolean,
      default: false
    },

    back: {
      type: Boolean,
      default: false
    },

    confirmClose: {
      type: Boolean,
      default: false
    },

    confirmCloseText: {
      type: String,
      default: "Вы уверены, что хотите закрыть окно?"
    },

    callback: Function
  },

  data() {
    return {
      showContent: false,
      windowScrollY: 0,
      showCloseConfirmation: false
    };
  },

  methods: {
    handleClose() {
      if (this.confirmClose) {
        this.showCloseConfirmation = true;
      } else {
        this.close();
      }
    },

    confirmClosePopup() {
      this.showCloseConfirmation = false;
      this.close();
    },

    cancelClose() {
      this.showCloseConfirmation = false;
    },

    close(cb = null) {
      this.showContent = false;
      modalHolder.removeInstance(this);

      if (detectIE()) {
        this.$emit("close");

        if (cb && typeof cb === "function") {
          cb();
        }
      } else {
        setTimeout(() => {
          this.$emit("close");

          if (cb && typeof cb === "function") {
            cb();
          }
        }, 200);
      }

      if (this.callback) {
        this.callback();
      }
    },

    scrollToTop() {
      this.$refs.modal.scrollTo(0, 0);
      this.$refs.modalContent.scrollTo(0, 0);
    }
  },

  beforeMount() {
    this.windowScrollY = window.scrollY;

    const modalCount =
      parseInt(document.body.getAttribute("data-modal-count")) || 0;

    document.body.setAttribute("data-modal-count", modalCount + 1);

    document.body.classList.add("_overflow_hidden");

    if (detectIE()) {
      this.showContent = true;
    } else {
      setTimeout(() => {
        this.showContent = true;
      }, 150);
    }

    modalHolder.addInstance(this);
  },

  beforeDestroy() {
    let modalCount = parseInt(document.body.getAttribute("data-modal-count"));

    if (!modalCount || modalCount <= 0) {
      modalCount = 1;
    }

    const newModalCount = modalCount - 1;

    document.body.setAttribute("data-modal-count", newModalCount);

    if (newModalCount <= 0) {
      document.body.classList.remove("_overflow_hidden");
    }

    window.scrollTo(0, this.windowScrollY);
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/media";

.modal {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  -webkit-overflow-scrolling: touch;
  background: rgba(#000f1c, 0.75);
  z-index: 20;

  &_mini {
    background: rgba(#000f1c, 0.9);
  }

  &--popup {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    * {
      box-sizing: border-box;
    }

    .modal {
      &__show-content {
        position: relative;
      }

      &__content {
        margin: auto;
        overflow-y: auto;
        width: auto;
        min-width: auto;
        height: auto;
        min-height: auto;
      }

      &__close {
        position: absolute;
        right: 0;
        left: auto;
        top: 0;
        z-index: 3;
        width: 50px;
        height: 50px;
        display: flex;
        justify-content: center;
        align-items: center;

        &:not(&_mobile) {
          display: none;
        }

        &:hover {
          background: var(--gray-2, #eaeaea);
        }

        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
  }

  &__backdrop {
    width: 100%;
    height: 100%;

    position: fixed;
    top: 0;
    left: 0;

    z-index: 1;

    cursor: pointer;
  }

  &__close {
    background: none;
    border: none;
    outline: none;

    position: fixed;
    left: 13.75vw;
    top: 60px;

    cursor: pointer;

    z-index: 2;

    &_mobile {
      display: none;
    }

    .svg-icon {
      width: 34px;
      height: 34px;

      @include respond-down(md) {
        width: 24px;
        height: 24px;
      }
    }

    @media (max-width: 1270px) {
      left: 3.75vw;
    }

    @include respond-down(lg) {
      position: fixed;
      top: 26px;
      right: 26px;
      left: auto;
    }

    @include respond-down(md) {
      top: 24px;
      right: 16px;
      display: none;

      &_mobile {
        display: block;
        position: absolute;
        z-index: 4;
      }
    }
  }

  &_mini &__close {
    left: 15vw;

    @media (max-width: 1270px) {
      left: 3.75vw;
    }

    @include respond-down(lg) {
      position: fixed;
      top: 26px;
      right: 26px;
      left: auto;
    }

    @include respond-down(md) {
      top: 24px;
      right: 16px;
    }
  }

  &__content {
    width: 80vw;

    min-width: 1030px;
    height: 100vh;
    overflow-y: scroll;

    background: #fff;

    margin-left: auto;

    position: relative;
    z-index: 2;

    @media (max-width: 1270px) {
      width: 90vw;
      min-width: auto;
    }

    @include respond-down(lg) {
      width: 100%;
      margin-top: 80px;

      min-width: auto;
      min-height: auto;

      height: calc(100vh - 80px);

      overflow-y: scroll;
    }

    @include respond-down(md) {
      margin: 0;
      height: calc(var(--vh, 1vh) * 100);
    }
  }

  &_mini &__content {
    width: 75vw;
    min-width: 950px;

    @include respond-down(lg) {
      width: 100%;
      min-width: auto;
    }
  }
}
</style>
